import React, { useState, useEffect } from 'react';

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  fallbackSrc?: string;
  blurDataUrl?: string;
  sizes?: string;
}

export function ImageWithFallback({
  src,
  alt,
  fallbackSrc = 'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?auto=format&fit=crop&q=80&w=10&blur=50',
  blurDataUrl,
  className = '',
  sizes = '100vw',
  ...props
}: ImageWithFallbackProps) {
  const [imgSrc, setImgSrc] = useState(blurDataUrl || fallbackSrc);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    // Create new image object
    const img = new Image();
    img.src = src;

    img.onload = () => {
      if (isMounted) {
        setImgSrc(src);
        setIsLoading(false);
      }
    };

    img.onerror = () => {
      if (isMounted) {
        setImgSrc(fallbackSrc);
        setIsLoading(false);
        setError(true);
      }
    };

    return () => {
      isMounted = false;
      img.onload = null;
      img.onerror = null;
    };
  }, [src, fallbackSrc, blurDataUrl]);

  // Generate srcSet for responsive images
  const generateSrcSet = () => {
    if (error) return undefined;
    
    const widths = [320, 640, 768, 1024, 1280, 1536];
    const srcSet = widths
      .map(width => {
        const url = new URL(src);
        url.searchParams.set('w', width.toString());
        return `${url.toString()} ${width}w`;
      })
      .join(', ');

    return srcSet;
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <img
        src={imgSrc}
        alt={alt}
        className={`transition-opacity duration-300 ${
          isLoading ? 'opacity-60 blur-sm' : 'opacity-100 blur-0'
        }`}
        loading="lazy"
        srcSet={generateSrcSet()}
        sizes={sizes}
        {...props}
      />
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 animate-pulse" />
      )}
    </div>
  );
}